import { api, formatApiError } from "./api";

// Connexion par empreinte digitale (WebAuthn) — encapsule les conversions
// entre le format base64url renvoyé par le serveur et les ArrayBuffer
// attendus par l'API navigateur navigator.credentials.
//
// La clé PRIVÉE générée par le capteur d'empreinte ne quitte JAMAIS
// l'appareil (stockée dans une puce sécurisée) — seule la clé PUBLIQUE
// correspondante est envoyée au serveur. C'est ce qui garantit qu'une
// empreinte enregistrée sur un appareil est physiquement inutilisable sur
// un autre.

function base64urlToBuffer(base64url) {
  const padding = "=".repeat((4 - (base64url.length % 4)) % 4);
  const base64 = (base64url + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64);
  const buffer = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) buffer[i] = raw.charCodeAt(i);
  return buffer.buffer;
}

function bufferToBase64url(buffer) {
  const bytes = new Uint8Array(buffer);
  let str = "";
  for (const b of bytes) str += String.fromCharCode(b);
  return btoa(str).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

export function isWebauthnAvailable() {
  return typeof window !== "undefined" && !!window.PublicKeyCredential && !!navigator.credentials;
}

export async function registerFingerprint(deviceLabel) {
  const { data } = await api.post("/webauthn/register/options");
  const options = JSON.parse(data.options);
  options.challenge = base64urlToBuffer(options.challenge);
  options.user.id = base64urlToBuffer(options.user.id);
  if (options.excludeCredentials) {
    options.excludeCredentials = options.excludeCredentials.map((c) => ({ ...c, id: base64urlToBuffer(c.id) }));
  }
  const credential = await navigator.credentials.create({ publicKey: options });
  if (!credential) throw new Error("Aucune empreinte capturée");
  const credentialJSON = {
    id: credential.id,
    rawId: bufferToBase64url(credential.rawId),
    type: credential.type,
    response: {
      clientDataJSON: bufferToBase64url(credential.response.clientDataJSON),
      attestationObject: bufferToBase64url(credential.response.attestationObject),
    },
  };
  await api.post("/webauthn/register/verify", { credential: credentialJSON, device_label: deviceLabel || "" });
}

export async function loginWithFingerprint(matricule) {
  const { data } = await api.post("/webauthn/login/options", { matricule });
  const options = JSON.parse(data.options);
  options.challenge = base64urlToBuffer(options.challenge);
  if (options.allowCredentials) {
    options.allowCredentials = options.allowCredentials.map((c) => ({ ...c, id: base64urlToBuffer(c.id) }));
  }
  const assertion = await navigator.credentials.get({ publicKey: options });
  if (!assertion) throw new Error("Empreinte non reconnue");
  const credentialJSON = {
    id: assertion.id,
    rawId: bufferToBase64url(assertion.rawId),
    type: assertion.type,
    response: {
      clientDataJSON: bufferToBase64url(assertion.response.clientDataJSON),
      authenticatorData: bufferToBase64url(assertion.response.authenticatorData),
      signature: bufferToBase64url(assertion.response.signature),
      userHandle: assertion.response.userHandle ? bufferToBase64url(assertion.response.userHandle) : null,
    },
  };
  const { data: result } = await api.post("/webauthn/login/verify", { matricule, credential: credentialJSON });
  return result;
}

export function formatWebauthnError(err) {
  if (err?.name === "NotAllowedError") return "Action annulée ou délai dépassé.";
  if (err?.name === "InvalidStateError") return "Cette empreinte est déjà enregistrée sur cet appareil.";
  if (err?.response) return formatApiError(err.response.data?.detail);
  return err?.message || "Une erreur est survenue.";
}
